import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

import { Corban } from "@/models/corban.model";
import {
  deactivateCorban,
  getCorbansByCreditPromoterIdPaginated,
  getCorbansPaginated,
} from "@/services/corban.service";
import { downloadConsumoCotasCorbanExcel } from "@/services/consignado/consume-quotes-corban.service";
import { useCallback, useEffect, useState } from "react";
import { CiBank, CiSearch } from "react-icons/ci";
import {
  FaFileExcel,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaUserPlus,
} from "react-icons/fa";
import CreditAuctionProposalsPagination from "../CreditAuctionProposalsPage/credit-auction-proposals-pagination";
import {
  EDGE_COLUMN_PX,
  tableCellStyles,
  tableHeaderStyles,
  tagButtonStyle,
} from "../FinancialShared/tableStyles";

import { routes } from "../../routes/index";

import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { documentFormatter } from "@/utils/documentFormatter";
import { useDebounce } from "@/utils/useDebounce";
import * as XLSX from "xlsx";
import ConsumoTable from "../ConsumoTable";
import ConsumoLoteTable from "../ConsumoLoteTable";
import UsersTable from "../UsersTablePage/UsersTable";

export default function CorbanTable({ creditPromoterId }: {creditPromoterId: string}) {
  const backgroundStyle = useConditionalBackground();

  const [search, setSearch] = useState("");
  const [consumoSearch, setConsumoSearch] = useState("");
  const [consumoPage, setConsumoPage] = useState(1);
  const [consumoPageSize, setConsumoPageSize] = useState(10);
  const [loadConsumoExcel, setLoadConsumoExcel] = useState(false);
  // const [loadLoteExcel, setLoadLoteExcel] = useState(false);
  const [loteSearch, setLoteSearch] = useState("");
  const [lotePage, setLotePage] = useState(1);
  const [lotePageSize, setLotePageSize] = useState(10);
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    setConsumoPage(1);
  }, [consumoSearch]);

  useEffect(() => {
    setLotePage(1);
  }, [loteSearch]);

  const [corbans, setCorbans] = useState<Corban[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCorban, setSelectedCorban] = useState<Corban | undefined>(
    undefined
  );
  const [tabIndex, setTabIndex] = useState(0);

  const fetchOrderedCorbans = useCallback(async () => {
    try {
      let response;
      if (creditPromoterId) {
        response = await getCorbansByCreditPromoterIdPaginated(
          page,
          pageSize,
          parseInt(creditPromoterId, 10),
          sortBy,
          sortDirection,
          debouncedSearch.trim()
        );
        setCorbans(response.data);
        setTotalPages(response.totalPages || 1);
        return;
      }
      response = await getCorbansPaginated(
        page,
        pageSize,
        sortBy,
        sortDirection,
        false,
        debouncedSearch.trim()
      );
      setCorbans(response.data);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error("Erro ao obter corbans:", error);
    }
  }, [page, pageSize, sortBy, sortDirection, debouncedSearch.trim()]);

  useEffect(() => {
    fetchOrderedCorbans();
  }, [fetchOrderedCorbans]);

  const handleSort = (field: any) => {
    setSortBy(field);
    setSortDirection((prevDirection) =>
      sortBy === field && prevDirection === "asc" ? "desc" : "asc"
    );
  };

  const FilteredCorbanData = corbans || [];



  const handleDownload = async () => {
    const exportData = FilteredCorbanData.map((corban) => ({
      ID: corban.id,
      Nome: corban.businessName,
      CNPJ: corban.document,
      "Razão Social": corban.legalName,
      "Responsável Operação": corban.FinancialSettings?.CommercialManager?.name,
      "Responsável Comercial":
        corban.FinancialSettings?.externalCommercialManager,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Corbans");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "corbans.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Arquivo Excel baixado com sucesso.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleDownloadConsumoExcel = async () => {
    setLoadConsumoExcel(true);
    try {
      const blob = await downloadConsumoCotasCorbanExcel(
        consumoPage,
        consumoPageSize,
        consumoSearch,
      );
      const url = window.URL.createObjectURL(
        new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "consumo-cotas-corban.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({
        title: "Excel exportado com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Erro ao exportar consumo:", error);
      toast({
        title: "Erro ao baixar o Excel",
        description: "Não foi possível gerar o arquivo. Tente novamente.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadConsumoExcel(false);
    }
  };

  // const handleDownloadLoteExcel = async () => {
  //   setLoadLoteExcel(true);
  //   try {
  //     // MOCKADO: aguardando o backend definir o endpoint de Excel do consumo
  //     // em lote (ver downloadConsultasInfoLoteExcel no service).
  //     const blob = await downloadConsultasInfoLoteExcel(loteSearch);
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = "consumo-consultas-lote.xlsx";
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(url);
  //     toast({
  //       title: "Excel exportado com sucesso.",
  //       status: "success",
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //   } catch (error) {
  //     console.error("Erro ao exportar consumo em lote:", error);
  //     toast({
  //       title: "Erro ao baixar o Excel",
  //       description: "Não foi possível gerar o arquivo. Tente novamente.",
  //       status: "error",
  //       duration: 4000,
  //       isClosable: true,
  //     });
  //   } finally {
  //     setLoadLoteExcel(false);
  //   }
  // };

  const handleDesactivateCorban = async (id: number) => {
    try {
      await deactivateCorban(id);
      toast({
        title: "Corban desativado com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await fetchOrderedCorbans();
    } catch (error) {
      toast({
        title: "Erro ao desativar Corban",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };



  return (
    <Tabs colorScheme="teal" width="100%" index={tabIndex} onChange={(index) => setTabIndex(index)}>
      <Box
        padding={{ base: "2", md: "4" }}
      display="flex"
      flexDirection="column"
      width={"100%"}
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="md"
      >
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "center" }}
          justifyContent="space-between"
          mb={4}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <CiBank size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              Corbans
            </Text>
            <TabList ml={6} borderBottom="none">
              <Tab
                fontFamily="Lexend Deca"
                fontWeight="bold"
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                color="whiteAlpha.800"
                _selected={{ color: "white", borderBottomColor: "white" }}
              >
                Cadastro
              </Tab>
              <Tab
                fontFamily="Lexend Deca"
                fontWeight="bold"
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                color="whiteAlpha.800"
                _selected={{ color: "white", borderBottomColor: "white" }}
              >
                Consulta Motor de Crédito
              </Tab>
              <Tab
                fontFamily="Lexend Deca"
                fontWeight="bold"
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                color="whiteAlpha.800"
                _selected={{ color: "white", borderBottomColor: "white" }}
              >
                Consulta de Margem
              </Tab>
            </TabList>
          </Flex>

        </Flex>

        {tabIndex === 0 && (
        <Flex
          mt={{ base: 4, md: 0 }}
          justifyContent={{ base: "space-between", md: "space-between" }}
          alignItems="center"
        >
          <InputGroup width={{ base: "18%", md: "30%" }}>
            <InputLeftElement mt={5} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              mt={4}
              placeholder="Pesquisar..."
              variant="filled"
              height={{ base: "56px", md: "48px" }}
              type="text"
              bg={"white"}
              maxLength={30}
              value={search}
              onChange={(ev) => setSearch(ev.target.value)}
              color="black"
              _focus={{ bg: "white" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
          <Flex align="center" gap={3} mt={4} ml="auto" mr={6}>
            <Link
              as={RouterLink}
              to={
                creditPromoterId
                  ? `${routes.find((route) => route.name === "corban-create")?.path}?creditPromoterId=${creditPromoterId}`
                  : routes.find((route) => route.name === "corban-create")
                      ?.path || "/"
              }
              bg="white"
              color="black"
              px={4}
              py={2}
              borderRadius="md"
              fontWeight="bold"
              _hover={{ bg: "gray.200" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize={{ base: "md", md: "md" }}
              fontFamily="Lexend Deca"
            >
              <FaUserPlus style={{ marginRight: "8px", fontSize: "20px" }} />
              Cadastrar Corban
            </Link>
            <IconButton
              icon={<FaFileExcel />}
              aria-label="Download Excel"
              onClick={handleDownload}
              colorScheme="green"
              size={"md"}
            />
          </Flex>

        </Flex>
        )}
        {tabIndex === 1 && (
          <Flex
            mt={{ base: 4, md: 0 }}
            justifyContent={{ base: "space-between", md: "flex-start" }}
            alignItems="flex-end"
            gap={3}
          >
            <InputGroup width={{ base: "calc(100% - 4rem)", md: "30%" }} flexShrink={0}>
              <InputLeftElement mt={5} pointerEvents="none">
                <CiSearch color="black" size="2rem" />
              </InputLeftElement>
              <Input
                mt={4}
                placeholder="Pesquisar..."
                variant="filled"
                height={{ base: "56px", md: "48px" }}
                type="text"
                bg={"white"}
                maxLength={30}
                value={consumoSearch}
                onChange={(ev) => setConsumoSearch(ev.target.value)}
                color="black"
                _focus={{ bg: "white" }}
                _hover={{ bg: "white" }}
                fontFamily="Lexend Deca"
              />
            </InputGroup>
            {loadConsumoExcel ? (
              <Spinner
                size="md"
                color="green.400"
                thickness="4px"
                speed="0.65s"
                mb={1}
              />
            ) : (
              <Tooltip label="Exportar Excel (filtro atual)" hasArrow placement="top">
                <IconButton
                  icon={<FaFileExcel />}
                  aria-label="Exportar consumo para Excel"
                  onClick={handleDownloadConsumoExcel}
                  colorScheme="green"
                  size="md"
                  mt={4}
                  h={{ base: "56px", md: "48px" }}
                  minW={{ base: "56px", md: "48px" }}
                />
              </Tooltip>
            )}
          </Flex>
        )}
        {tabIndex === 2 && (
          <Flex
            mt={{ base: 4, md: 0 }}
            justifyContent={{ base: "space-between", md: "flex-start" }}
            alignItems="flex-end"
            gap={3}
          >
            <InputGroup width={{ base: "calc(100% - 4rem)", md: "30%" }} flexShrink={0}>
              <InputLeftElement mt={5} pointerEvents="none">
                <CiSearch color="black" size="2rem" />
              </InputLeftElement>
              <Input
                mt={4}
                placeholder="Pesquisar..."
                variant="filled"
                height={{ base: "56px", md: "48px" }}
                type="text"
                bg={"white"}
                maxLength={30}
                value={loteSearch}
                onChange={(ev) => setLoteSearch(ev.target.value)}
                color="black"
                _focus={{ bg: "white" }}
                _hover={{ bg: "white" }}
                fontFamily="Lexend Deca"
              />
            </InputGroup>
            {/* {loadLoteExcel ? (
              <Spinner
                size="md"
                color="green.400"
                thickness="4px"
                speed="0.65s"
                mb={1}
              />
            ) : (
              <Tooltip label="Exportar Excel (filtro atual)" hasArrow placement="top">
                <IconButton
                  icon={<FaFileExcel />}
                  aria-label="Exportar consumo em lote para Excel"
                  onClick={handleDownloadLoteExcel}
                  colorScheme="green"
                  size="md"
                  mt={4}
                  h={{ base: "56px", md: "48px" }}
                  minW={{ base: "56px", md: "48px" }}
                />
              </Tooltip>
            )} */}
          </Flex>
        )}
      </Box>

        <TabPanels>
          <TabPanel px={0}>
      {FilteredCorbanData.length !== 0 ? (
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          px={0}
          py={{ base: 2, md: 4 }}
          mt={4}
        >
          <CreditAuctionProposalsPagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={FilteredCorbanData.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />

          <TableContainer borderRadius="lg" overflowX="auto">
            <Table variant="simple">
              <Thead {...backgroundStyle}>
                <Tr>
                  <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX} w="220px">
                    AÇÕES
                  </Th>
                  {[
                    { label: "ID", field: "id" },
                    { label: "CORBAN", field: "businessName" },
                    { label: "CNPJ", field: "document" },
                    { label: "RAZÃO SOCIAL", field: "legalName" },
                    {
                      label: "RESPONSÁVEL PELA OPERAÇÃO",
                      field: "commercialManager",
                    },
                  ].map(({ label, field }, idx, arr) => {
                    const sortIcon =
                      sortBy === field
                        ? sortDirection === "asc"
                          ? FaSortUp
                          : FaSortDown
                        : FaSort;
                    return (
                      <Th
                        key={field}
                        {...tableHeaderStyles}
                        px={idx === arr.length - 1 ? EDGE_COLUMN_PX : 1}
                        cursor="pointer"
                        userSelect="none"
                        onClick={() => handleSort(field)}
                        _hover={{ opacity: 0.85 }}
                      >
                        <Flex align="center" justify="center" gap={1.5}>
                          {label}
                          <Icon as={sortIcon} boxSize={3} />
                        </Flex>
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {FilteredCorbanData.map((corban) => (
                  <Tr key={corban.id}>
                    <Td
                      {...tableCellStyles}
                      px={EDGE_COLUMN_PX}
                      py={{ base: 2, md: 3 }}
                    >
                      <Flex
                        justify="center"
                        flexDirection="column"
                        gap={2}
                        alignItems="center"
                      >

                        <Button
                          size="md"
                          {...tagButtonStyle("blue")}
                          width="90%"
                          onClick={() => (
                            setSelectedCorban(corban), onOpen()
                          )}
                          py={2}
                          fontSize="sm"
                          fontWeight="bold"
                          fontFamily="Lexend Deca"
                        >
                          Usuários
                        </Button>
                        <Button
                          size="md"
                          {...tagButtonStyle("red")}
                          width="90%"
                          py={2}
                          fontSize="sm"
                          fontWeight="bold"
                          fontFamily="Lexend Deca"
                          onClick={() =>
                            corban.id && handleDesactivateCorban(corban.id)
                          }
                        >
                          Desativar Corban
                        </Button>
                      </Flex>
                    </Td>
                    <Td {...tableCellStyles}>
                      <Link
                        as={RouterLink}
                        to={`/corban/editar/${corban.id}`}
                        color="blue.500"
                      >
                        {corban.id}
                      </Link>
                    </Td>
                    <Td {...tableCellStyles}>{corban.businessName}</Td>
                    <Td {...tableCellStyles} whiteSpace="nowrap">
                      {documentFormatter(corban.document)}
                    </Td>
                    <Td {...tableCellStyles}>{corban.legalName}</Td>
                    <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                      {corban.masterName}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          <CreditAuctionProposalsPagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={FilteredCorbanData.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </Box>
      ) : (
        <Heading mt={100} textAlign={"center"} color="gray.600">
          Não há corbans cadastrados
        </Heading>
      )}
          </TabPanel>
          <TabPanel px={0}>
            <ConsumoTable
              searchTerm={consumoSearch}
              page={consumoPage}
              pageSize={consumoPageSize}
              onPageChange={setConsumoPage}
              onPageSizeChange={(size) => {
                setConsumoPageSize(size);
                setConsumoPage(1);
              }}
            />
          </TabPanel>
          <TabPanel px={0}>
            <ConsumoLoteTable
              searchTerm={loteSearch}
              page={lotePage}
              pageSize={lotePageSize}
              onPageChange={setLotePage}
              onPageSizeChange={(size) => {
                setLotePageSize(size);
                setLotePage(1);
              }}
            />
          </TabPanel>
        </TabPanels>

      <Modal isOpen={isOpen} onClose={onClose} size="100%">
        <ModalOverlay />
        <ModalContent width={"85%"}>
          <ModalHeader></ModalHeader>
          <ModalCloseButton />
          <ModalBody width={"98%"}>
            <Box>
              <UsersTable corban={selectedCorban} />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="secondary" mr={3} onClick={onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


      </Box>
    </Tabs>
  );
}
